import { AxiosResponse } from "axios";

import {
  IClinicianForm,
  ICompletedSurveyResponse,
  ISurveyAnswerForEdit,
  IUpdateSurveyAnswerRequest,
  IWardSurveyDetail,
  IWardSurveyOverview,
} from "../../@types/admin/AdminSurvey";
import { IAuditLog } from "../../@types/AuditLog";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * Surveys & PROMs admin page (EN-5164), migrated from the Retool Surveys app
 * (`moduleSurveysProms`).
 *
 * ⚠️ Retool called `HospitalSurveys` / `HospitalSurveyDetails` and their two
 * exports. All four are `[Obsolete]` aliases on `SurveysController` that
 * delegate to the canonical `Ward*` routes and are documented as "will be
 * removed in a future release" — so the portal calls the canonical ones. Same
 * shapes, same service methods behind them.
 */
export const surveysService = {
  /** Every clinician form, for the report picker. */
  async getClinicianForms(): Promise<AxiosResponse<IClinicianForm[]>> {
    return api.get<IClinicianForm[]>("/Retool/Surveys/AllClinicianForms");
  },

  /** PROMs per ward. Canonical route (Retool used `HospitalSurveys`). */
  async getWardSurveys(): Promise<AxiosResponse<IWardSurveyOverview[]>> {
    return api.get<IWardSurveyOverview[]>("/Retool/Surveys/WardSurveys");
  },

  /** The surveys configured on one ward, with their completion counts. */
  async getWardSurveyDetails(
    wardId: number
  ): Promise<AxiosResponse<IWardSurveyDetail[]>> {
    return api.get<IWardSurveyDetail[]>("/Retool/Surveys/WardSurveyDetails", {
      params: { wardId },
    });
  },

  /** Completed responses for one survey. */
  async getCompletedResponses(
    surveyId: string
  ): Promise<AxiosResponse<ICompletedSurveyResponse[]>> {
    return api.get<ICompletedSurveyResponse[]>(
      "/Retool/Surveys/CompletedResponses",
      { params: { surveyId } }
    );
  },

  /** One response's questions, options and current answers, for editing. */
  async getSurveyAnswerForEdit(
    surveyAnswerId: string
  ): Promise<AxiosResponse<ISurveyAnswerForEdit>> {
    return api.get<ISurveyAnswerForEdit>(
      `/Retool/Surveys/SurveyAnswers/${surveyAnswerId}/Edit`
    );
  },

  /**
   * ⚠️ Writes a submitted patient survey answer — a clinical record. Every
   * call is audited server-side; see `getSurveyAnswerAuditLogs`.
   */
  async updateSurveyAnswer(
    surveyAnswerId: string,
    request: IUpdateSurveyAnswerRequest
  ): Promise<AxiosResponse<void>> {
    return api.put<void>(
      `/Retool/Surveys/SurveyAnswers/${surveyAnswerId}`,
      request
    );
  },

  /** The audit trail for one answer, over a date window. */
  async getSurveyAnswerAuditLogs(
    surveyAnswerId: string,
    startDate: string,
    endDate: string,
    page: number,
    pageSize: number
  ): Promise<AxiosResponse<IPaginatedResponse<IAuditLog[]>>> {
    return api.get<IPaginatedResponse<IAuditLog[]>>(
      `/Retool/Surveys/SurveyAnswers/${surveyAnswerId}/AuditLogs`,
      { params: { startDate, endDate, page, pageSize } }
    );
  },
};

/**
 * The four exports, as `useFileDownload` requests. All are **POST with a JSON
 * body** — the ticket lists them as GETs, which the export contradicts and so
 * does the controller.
 *
 * Each returns a real `File(...)`, so the base64 envelope Retool's success
 * scripts decoded is a Retool transport detail, not the API contract (same
 * correction as EN-5159).
 */
export const surveyExportRequests = {
  clinicianFormReport(
    surveyId: string,
    fromDate?: string | null,
    toDate?: string | null
  ) {
    return {
      url: "/Retool/Surveys/GenerateSurveyReport",
      method: "POST" as const,
      data: { surveyId, fromDate: fromDate || null, toDate: toDate || null },
    };
  },

  wardSurveys() {
    return {
      url: "/Retool/Surveys/ExportWardSurveys",
      method: "POST" as const,
      data: {},
    };
  },

  wardSurveyDetails(wardId: number) {
    return {
      url: "/Retool/Surveys/ExportWardSurveyDetails",
      method: "POST" as const,
      data: { wardId },
    };
  },

  completedResponses(surveyId: string) {
    return {
      url: "/Retool/Surveys/ExportCompletedResponses",
      method: "POST" as const,
      data: { surveyId },
    };
  },
};
